import notEmpty from '@/lib/helpers/not-empty';
import Validation from '@/lib/models/validation';

import {
  VALID,
  INVALID,
  UNPROCESSED,
  DEFAULT_IS_EMPTY_VALIDATION_MESSAGE,
} from '@/lib/constants/validations';

import {
  CHANGE_VALIDATIONS_STATES,
  CHANGE_VALUE_FIELD,
} from '@/lib/constants/mutations';

import store from '@/store';

import isOptionalAcceptedValue from '@/lib/helpers/optional-accepted-value';

const preCommitStateChange = (name, newValue, stateValues) => {
  const copyStates = Object.assign({}, stateValues);

  copyStates[name] = newValue;

  return copyStates;
};

const addNotEmptyValidation = (model) => {
  const extractValueFn = values => values[model.name];
  let errorMessage = `${model.name} ${DEFAULT_IS_EMPTY_VALIDATION_MESSAGE}`;
  if (model.customNotEmptyErrorMessage) {
    errorMessage = model.customNotEmptyErrorMessage;
  }

  const newValidation = new Validation({
    condition: notEmpty,
    errorMessage,
    name: 'not_optional',
    attachField: model,
    extractValueFn,
  }, { addFirst: true });

  return newValidation;
};

class ValidationManager {
  constructor(context) {
    this.context = context;

    if (!context.isOptional) {
      addNotEmptyValidation(context);
    }
  }

  get activeValidations() {
    if (isOptionalAcceptedValue(this.context, this.context.value)) {
      return [];
    }

    return this.context.validations;
  }

  get isUnprocessed() {
    return this.activeValidations.length !== 0
    && this.activeValidations
      .every(v => v.isUnprocessed);
  }

  get isValid() {
    return this.activeValidations.length === 0
    || this.activeValidations.every(v => v.isValid);
  }

  get isInvalid() {
    return this.activeValidations
      .some(v => v.isInvalid);
  }

  get validationState() {
    if (this.isUnprocessed) {
      return UNPROCESSED;
    }
    if (this.isValid) {
      return VALID;
    }
    if (this.isInvalid) {
      return INVALID;
    }
    return UNPROCESSED;
  }

  forceValidations() {
    const newValidationStates = this.__doValidations(store.state.formData.values);

    store.commit(
      CHANGE_VALIDATIONS_STATES,
      {
        newValidationStates,
      },
    );
  }

  runChangeValidations(newValue, { resetValidationsIfEmpty = false }) {
    const preCommitValues = this.__preCommitValues(newValue);

    let validationStates = [];

    if (resetValidationsIfEmpty && !notEmpty(newValue)) {
      validationStates = this.__resetAllValidations();
    } else {
      validationStates = this.__doValidations(
        preCommitValues,
      );
    }

    this.__commitValueChangeWithValidations(newValue, validationStates, true);
  }

  __resetAllValidations() {
    return this.context.validations.map(v => v.resetValidation());
  }

  runInstantValidations(newValue) {
    const preCommitValues = this.__preCommitValues(newValue);
    const validationStates = this.__doValidations(
      preCommitValues,
      validation => validation.isInvalid,
    );

    this.__commitValueChangeWithValidations(newValue, validationStates, false);
  }

  __doValidations(valuesToValidate, fnToFilter = () => true) {
    const { validations } = this.context;

    // No need to run validations if it is optional
    if (isOptionalAcceptedValue(this.context, valuesToValidate[this.context.name])) {
      return [];
    }

    const validationsToRun = validations
      .filter(fnToFilter);

    const statesToReturn = [];

    validationsToRun.every((validation) => {
      const state = validation.triggerValidation(valuesToValidate);
      statesToReturn.push(state);

      return state.newValidity === VALID;
    });

    return statesToReturn;
  }


  __commitValueChangeWithValidations(newValue, newValidationStates, edited) {
    const options = {
      name: this.context.name,
      newValue,
      newValidationStates,
      edited,
    };

    store.commit(
      CHANGE_VALUE_FIELD,
      options,
    );
  }

  __preCommitValues(newValue) {
    const preCommitValues = preCommitStateChange(
      this.context.name,
      newValue,
      store.state.formData.values,
    );

    return preCommitValues;
  }
}

export default ValidationManager;
